import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { CheckCircle2, FileSpreadsheet, DollarSign, Users, Wand2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = window.location.origin;

export function ImportConfirm({ batch, onClose }) {
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    if (!batch) return;
    let alive = true;
    const run = async () => {
      setLoading(true);
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${API_URL}/api/financial/plans/build-from-titles?dry_run=true`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({}),
        });
        const data = await res.json();
        if (alive && res.ok) setPreview(data);
      } catch { /* noop */ }
      finally { if (alive) setLoading(false); }
    };
    run();
    return () => { alive = false; };
  }, [batch]);

  const apply = async () => {
    setApplying(true);
    try {
      const token = localStorage.getItem('token');
      const res = await fetch(`${API_URL}/api/financial/plans/build-from-titles?dry_run=false`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({}),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Erro');
      setDone(data);
      toast.success(`${data.summary?.created || 0} plano(s) criado(s), ${data.summary?.titles_linked || 0} título(s) vinculado(s)`);
    } catch (e) { toast.error(e.message); }
    finally { setApplying(false); }
  };

  if (!batch) return null;

  const summary = preview?.summary;
  const turmas = preview?.groups ? new Set(preview.groups.map(g => g.class_group_id)).size : 0;

  return (
    <Card className="border-green-200 bg-green-50">
      <CardHeader>
        <div className="flex items-center gap-3">
          <CheckCircle2 className="w-8 h-8 text-green-500" />
          <div>
            <CardTitle className="text-green-700">Importação Concluída!</CardTitle>
            <p className="text-sm text-green-600">{batch.filename}</p>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="text-center p-4 bg-white rounded-lg">
            <FileSpreadsheet className="w-6 h-6 mx-auto text-purple-500 mb-2" />
            <p className="text-2xl font-bold">{batch.imported_rows}</p>
            <p className="text-sm text-muted-foreground">Títulos Importados</p>
          </div>
          <div className="text-center p-4 bg-white rounded-lg">
            <DollarSign className="w-6 h-6 mx-auto text-green-500 mb-2" />
            <p className="text-2xl font-bold">
              R$ {batch.importable_amount?.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}
            </p>
            <p className="text-sm text-muted-foreground">Valor Total</p>
          </div>
          <div className="text-center p-4 bg-white rounded-lg">
            <Users className="w-6 h-6 mx-auto text-blue-500 mb-2" />
            <p className="text-2xl font-bold">{batch.valid_rows}</p>
            <p className="text-sm text-muted-foreground">Alunos Vinculados</p>
          </div>
        </div>

        {/* Wizard de Importação Inteligente — sugere build-from-titles */}
        {!done && (
          <div className="rounded-lg border-2 border-violet-200 bg-violet-50 p-4 mb-4" data-testid="post-import-build-card">
            <div className="flex items-start gap-3">
              <Wand2 className="w-6 h-6 text-violet-600 shrink-0 mt-0.5" />
              <div className="flex-1">
                <p className="text-sm font-bold text-violet-900">Gerar planos financeiros automaticamente?</p>
                {loading && (
                  <p className="text-xs text-violet-700 flex items-center gap-1 mt-1"><Loader2 className="w-3 h-3 animate-spin" /> Calculando prévia…</p>
                )}
                {preview && summary && (
                  <p className="text-xs text-violet-800 mt-1">
                    Vamos criar <strong>{summary.created}</strong> plano(s) cobrindo <strong>{turmas}</strong> turma(s),
                    alinhados com <strong>{summary.titles_linked}</strong> título(s) e <strong>{summary.students_linked}</strong> aluno(s).
                    {summary.skipped_no_class > 0 && ` (${summary.skipped_no_class} sem turma serão ignorados)`}
                  </p>
                )}
                <div className="flex gap-2 mt-2">
                  <Button
                    onClick={apply}
                    disabled={applying || loading || !summary || summary.created === 0}
                    className="bg-violet-600 hover:bg-violet-700 h-8 text-xs"
                    data-testid="post-import-build-apply"
                  >
                    {applying ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Wand2 className="w-3 h-3 mr-1" />}
                    Confirmar e gerar
                  </Button>
                  <Button variant="ghost" onClick={() => setDone({ skipped: true })} className="h-8 text-xs">
                    Pular
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
        {done && !done.skipped && (
          <div className="rounded-lg border-2 border-emerald-300 bg-emerald-50 p-3 mb-4" data-testid="post-import-build-done">
            <p className="text-sm font-semibold text-emerald-900">
              ✅ {done.summary?.created || 0} plano(s) criado(s), {done.summary?.titles_linked || 0} título(s) vinculado(s).
            </p>
          </div>
        )}

        <div className="flex justify-center">
          <Button onClick={onClose}>
            Voltar ao Dashboard
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
